// The styled `data-turbo-confirm` dialog, which a caller drives either with a
// plain string or with a JSON payload. One `<dialog>` serves every confirm on the
// page, so a title or button label one caller names has to be undone for the next
// caller that does not. The markup's own copy is that fallback, read once per
// dialog element before anything overwrites it.

import { config } from '@hotwired/turbo'

const DEFAULTS = new WeakMap()

const TITLE_SELECTOR = '.modal__title'
const CONFIRM_BUTTON_SELECTOR = '[value="confirm"]'

export const parseConfirmPayload = (message) => {
  const text = message == null ? '' : String(message)
  if (!text.trim().startsWith('{')) return { message: text, variant: 'red' }

  const data = JSON.parse(text)

  return {
    message: data.message,
    variant: data.variant || 'red',
    title: data.title,
    confirmLabel: data.confirm_label
  }
}

const defaultsFor = (dialog) => {
  if (!DEFAULTS.has(dialog)) {
    DEFAULTS.set(dialog, {
      title: dialog.querySelector(TITLE_SELECTOR)?.textContent.trim() ?? '',
      confirmLabel: dialog.querySelector(CONFIRM_BUTTON_SELECTOR)?.textContent.trim() ?? ''
    })
  }

  return DEFAULTS.get(dialog)
}

export const applyConfirmPayload = (dialog, payload) => {
  // Before the writes below, or the first confirm to name a title is remembered
  // as the default for every confirm after it.
  const fallback = defaultsFor(dialog)

  const title = dialog.querySelector(TITLE_SELECTOR)
  const confirmButton = dialog.querySelector(CONFIRM_BUTTON_SELECTOR)

  if (title) title.textContent = payload.title || fallback.title

  const body = dialog.querySelector('p')
  if (body) body.textContent = payload.message

  if (confirmButton) {
    confirmButton.textContent = payload.confirmLabel || fallback.confirmLabel
    confirmButton.className = `btn btn-rounded btn-${payload.variant}`
  }
}

// Resolved per call: `Turbo.setConfirmMethod` installs the styled dialog above
// after this module loads, and a layout without `#turbo-confirm` never does.
export const askForConfirmation = (message, element, submitter) => {
  const confirmMethod = typeof config.forms.confirm === 'function'
    ? config.forms.confirm
    : (text) => Promise.resolve(window.confirm(text))

  return Promise.resolve(confirmMethod(message, element, submitter))
}
